"use client";

import React from "react";
import { Button } from "../button";
import { ResponsiveModal } from "../responsive-modal";
import { CentralizedDrawer } from "../global-drawer-stack";
import { Calendar } from "lucide-react";
import { useMediaQuery } from "../../hooks/use-media-query";
import { useCalendarContext } from "../../lib/calendar-context";
import { useAccountStatus } from "../../hooks/use-api";
import type { CalendarPlatform, MeetingFormData } from "./types/meeting";
import { useMeetingFormState } from "./hooks/useMeetingFormState";
import { useMeetingSubmission } from "./hooks/useMeetingSubmission";
import { useMeetingModalState } from "./hooks/useMeetingModalState";
import { MeetingFormFields } from "./components/MeetingFormFields";
import { MeetingFormFieldsMobile } from "./components/MeetingFormFieldsMobile";
import { ConfirmationModal } from "./components/ConfirmationModal";
import { SuccessModal } from "./components/SuccessModal";
import { AttendeeInfoModal } from "./components/AttendeeInfoModal";
import { MODAL_IDS } from "./constants/meetingConstants";

interface CreateMeetingModalProps {
  isOpen: boolean;
  onClose: () => void;
  globalModalId?: string;
  initialDate?: Date;
  initialDuration?: number;
  customerName?: string;
  /** Pre-populate the attendee list (e.g. the contact/company person being scheduled). */
  customerEmail?: string;
  className?: string;
  // Optional custom attendees selector renderer to integrate app-specific components
  renderAttendeesSelector?: (args: {
    currentEmails: string[];
    setEmails: (emails: string[]) => void;
    disabled: boolean;
  }) => React.ReactNode;
  // Optional props for advanced contact search integration
  contacts?: Array<{ id: string; name: string; email: string; company_name?: string }>;
  onSearchContacts?: (searchTerm: string) => Promise<Array<{ id: string; name: string; email: string; company_name?: string }>>;
  isLoadingContacts?: boolean;
  // Optional ContactPopover component for attendee chips
  ContactPopoverComponent?: React.ComponentType<{ name: string; email: string; children: React.ReactNode }>;
  /**
   * Optional AI-assist slot rendered at the top of the form. The host (e.g. Hub)
   * provides a component that takes a natural-language prompt, calls its own
   * AI endpoint, and applies the extracted fields via `setFields`. Kept as a
   * render prop so this shared package never depends on an app-specific API.
   */
  renderAiAssist?: (api: {
    setFields: (
      fields: Partial<
        Pick<MeetingFormData, "title" | "description" | "location" | "startDate" | "startTime" | "duration">
      >
    ) => void;
    disabled: boolean;
  }) => React.ReactNode;
}

export const CreateMeetingModal = ({
  isOpen,
  onClose,
  globalModalId = MODAL_IDS.CREATE_MEETING,
  initialDate = new Date(),
  initialDuration = 60,
  customerName,
  customerEmail,
  className,
  renderAttendeesSelector,
  ContactPopoverComponent,
  contacts: propContacts,
  onSearchContacts: propOnSearchContacts,
  isLoadingContacts: propIsLoadingContacts,
  renderAiAssist,
}: CreateMeetingModalProps) => {
  // Get contact search functionality from CalendarContext
  const calendarContext = useCalendarContext();
  const contacts = propContacts || calendarContext.contacts;
  const onSearchContacts = propOnSearchContacts || calendarContext.onSearchContacts;
  const isLoadingContacts = propIsLoadingContacts || calendarContext.isLoadingContacts;

  // Determine which calendar platforms this user can actually create on.
  const { data: accountStatus } = useAccountStatus();
  const availablePlatforms = React.useMemo<CalendarPlatform[]>(() => {
    const platforms: CalendarPlatform[] = [];
    if (accountStatus?.google?.primary || accountStatus?.google?.secondary) {
      platforms.push("google");
    }
    if (accountStatus?.microsoft) {
      platforms.push("microsoft");
    }
    if (accountStatus?.wtcEmailCalendarBridgeConfigured) {
      platforms.push("professional-email");
    }
    // Fall back to all options until account status resolves.
    return platforms.length > 0
      ? platforms
      : ["google", "microsoft", "professional-email"];
  }, [accountStatus]);

  // Use custom hooks for state management
  const formState = useMeetingFormState(
    initialDate,
    initialDuration,
    customerName,
    availablePlatforms[0],
    customerEmail
  );
  const submissionState = useMeetingSubmission();
  const modalState = useMeetingModalState();

  // If account status resolves after first render and the selected platform is no
  // longer offered, fall back to the first available platform.
  const { formData: currentFormData, handleInputChange } = formState;
  React.useEffect(() => {
    if (!availablePlatforms.includes(currentFormData.platform)) {
      handleInputChange("platform", availablePlatforms[0]);
    }
  }, [availablePlatforms, currentFormData.platform, handleInputChange]);

  const isMobile = useMediaQuery("(max-width: 768px)");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    modalState.openConfirmation();
  };

  const handleConfirmSubmit = async () => {
    await submissionState.handleConfirmSubmit(formState.formData);
    modalState.closeConfirmation();
  };

  const handleClose = () => {
    if (!submissionState.isSubmitting) {
      submissionState.resetSubmission();
      modalState.resetModals();
      formState.setAttendeeInput("");
      onClose();
    }
  };

  const setAttendeesEmails = (emails: string[]) => {
    formState.handleInputChange("attendees", emails.join(", "));
  };

  // Apply AI-extracted fields to the form (used by the optional renderAiAssist
  // slot). Attendees/platform are intentionally NOT settable here — attendees
  // stay manual, platform is account-driven.
  const aiSetFields = React.useCallback(
    (
      fields: Partial<
        Pick<MeetingFormData, "title" | "description" | "location" | "startDate" | "startTime" | "duration">
      >
    ) => {
      (Object.entries(fields) as Array<[keyof MeetingFormData, string | number | undefined]>).forEach(
        ([field, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            formState.handleInputChange(field, value);
          }
        }
      );
    },
    [formState]
  );

  // Render confirmation modal
  if (modalState.showConfirmation) {
    return (
      <ConfirmationModal
        isOpen={isOpen}
        onClose={modalState.closeConfirmation}
        onConfirm={handleConfirmSubmit}
        formData={formState.formData}
        currentAttendees={formState.currentAttendees}
        isSubmitting={submissionState.isSubmitting}
      />
    );
  }

  // Render attendee info modal
  if (modalState.showAttendeeInfo) {
    return (
      <AttendeeInfoModal
        isOpen={isOpen}
        onClose={modalState.closeAttendeeInfo}
      />
    );
  }

  // Render success modal
  if (submissionState.isSubmitted) {
    return (
      <SuccessModal
        isOpen={isOpen}
        onClose={handleClose}
        formData={formState.formData}
        responseData={submissionState.responseData}
        currentAttendees={formState.currentAttendees}
      />
    );
  }

  // Mobile version using CentralizedDrawer
  if (isMobile) {
    return (
      <CentralizedDrawer
        drawerId={MODAL_IDS.MOBILE_CREATE_MEETING}
        side="bottom"
        title="Create New Meeting"
        description="Schedule a meeting in your calendar with team members and clients"
        showCloseButton={true}
        enableSwipeGesture={true}
      >
        <div className="flex-1 overflow-y-auto p-4">
          <form onSubmit={handleSubmit} className="space-y-6">
            {renderAiAssist?.({ setFields: aiSetFields, disabled: submissionState.isSubmitting })}
            <MeetingFormFieldsMobile
              formData={formState.formData}
              onInputChange={formState.handleInputChange}
              onAddTeamMember={formState.handleAddTeamMember}
              onRemoveAttendee={formState.handleRemoveAttendee}
              currentAttendees={formState.currentAttendees}
              onAttendeesChange={setAttendeesEmails}
              onOpenAttendeeInfo={modalState.openAttendeeInfo}
              isSubmitting={submissionState.isSubmitting}
              submitError={submissionState.submitError}
              availablePlatforms={availablePlatforms}
              renderAttendeesSelector={renderAttendeesSelector}
              contacts={contacts}
              onSearchContacts={onSearchContacts}
              isLoadingContacts={isLoadingContacts}
              ContactPopoverComponent={ContactPopoverComponent}
            />
          </form>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={submissionState.isSubmitting}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submissionState.isSubmitting}
            className="flex-1"
          >
            {submissionState.isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating...
              </>
            ) : (
              <>
                <Calendar className="h-4 w-4 mr-2" />
                Create Meeting
              </>
            )}
          </Button>
        </div>
      </CentralizedDrawer>
    );
  }

  // Desktop version using ResponsiveModal
  // 🚨 CRITICAL: Uses ResponsiveModal with globalModalId for proper z-index stacking
  // This was fixed from Dialog to ResponsiveModal to participate in modal stack
  // See MODAL_STACKING_FIX.md for complete guide
  return (
    <ResponsiveModal
      open={isOpen}
      onOpenChange={handleClose}
      globalModalId={globalModalId}
      title="Create New Meeting"
      description="Schedule a meeting in your calendar with team members and clients."
      maxWidth="4xl"
      className="max-h-[90vh] overflow-y-auto"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {renderAiAssist?.({ setFields: aiSetFields, disabled: submissionState.isSubmitting })}
        <MeetingFormFields
          formData={formState.formData}
          onInputChange={formState.handleInputChange}
          onAddTeamMember={formState.handleAddTeamMember}
          onRemoveAttendee={formState.handleRemoveAttendee}
          currentAttendees={formState.currentAttendees}
          onAttendeesChange={setAttendeesEmails}
          onOpenAttendeeInfo={modalState.openAttendeeInfo}
          isSubmitting={submissionState.isSubmitting}
          submitError={submissionState.submitError}
          availablePlatforms={availablePlatforms}
          renderAttendeesSelector={renderAttendeesSelector}
          contacts={contacts}
          onSearchContacts={onSearchContacts}
          isLoadingContacts={isLoadingContacts}
          ContactPopoverComponent={ContactPopoverComponent}
        />

        {/* Submit Buttons */}
        <div className="flex gap-4 pt-6">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={submissionState.isSubmitting}
            className="flex-1 border-gray-300 text-slate-700 hover:bg-card-50 hover:border-gray-400 transition-colors"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submissionState.isSubmitting}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white shadow-md transition-colors"
          >
            {submissionState.isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating...
              </>
            ) : (
              <>
                <Calendar className="h-4 w-4 mr-2" />
                Create Meeting
              </>
            )}
          </Button>
        </div>
      </form>
    </ResponsiveModal>
  );
};
